import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Loader2,
  Mail,
  MapPin,
  Phone,
  User,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  getCoachingSessionPublicApi,
  getCoachingAvailabilityApi,
  createCoachingBookingApi,
} from '../lib/api';
import { getTodayPHT, formatHour } from '../lib/format';

const formatHourRange = (start, end) => `${formatHour(start)}–${formatHour(end)}`;

const formatHourShort = (h) => {
  const hh = h >= 24 ? h - 24 : h;
  if (hh === 0) return '12 AM';
  if (hh === 12) return '12 PM';
  if (hh < 12) return `${hh} AM`;
  return `${hh - 12} PM`;
};

// Strip a leading 'Coach ' prefix so the page never renders 'with Coach Coach X'
// (defensive — the backend also normalizes on save, this covers pre-existing data).
const stripCoachPrefix = (name) => {
  let n = (name || '').trim();
  while (n.toLowerCase().startsWith('coach ')) {
    n = n.slice(6).trim();
  }
  return n;
};

export default function CoachingBooking() {
  const { code } = useParams();
  const navigate = useNavigate();
  const todayPHT = getTodayPHT();

  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedDate, setSelectedDate] = useState(todayPHT);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedStart, setSelectedStart] = useState(null);
  const [duration, setDuration] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Availability
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Form fields
  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [playerEmail, setPlayerEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch session (coach identity + pricing rules)
  useEffect(() => {
    (async () => {
      try {
        const res = await getCoachingSessionPublicApi(code);
        setSessionData(res.data);
        // Default court selection: first court
        if (res.data?.courts?.length) {
          setSelectedCourt(res.data.courts[0].id);
        }
      } catch (err) {
        setError('This coaching link is not valid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  // Fetch availability whenever date or session loads
  useEffect(() => {
    if (!sessionData || !selectedDate) return;
    (async () => {
      setLoadingAvailability(true);
      setSelectedStart(null);
      try {
        const res = await getCoachingAvailabilityApi(code, selectedDate);
        setAvailability(res.data);
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      } finally {
        setLoadingAvailability(false);
      }
    })();
  }, [code, sessionData, selectedDate]);

  // Horizontal quick-date chips (next 7 days)
  const dateChips = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(todayPHT + 'T00:00:00');
      d.setDate(d.getDate() + i);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
  }, [todayPHT]);

  // Build slot grid for the selected court (all operating hours; mark as available/unavailable)
  const courtSlots = useMemo(() => {
    if (!availability || !selectedCourt || !sessionData) return [];
    const startH = sessionData.operating_hours?.start ?? 6;
    const endH = sessionData.operating_hours?.end ?? 24;
    const court = availability.courts?.[selectedCourt];
    const availableSet = new Set((court?.available_slots || []).map((s) => s.hour));
    const rateByHour = {};
    (court?.available_slots || []).forEach((s) => {
      rateByHour[s.hour] = s.rate;
    });
    return Array.from({ length: endH - startH }, (_, i) => {
      const hour = startH + i;
      return {
        hour,
        available: availableSet.has(hour),
        rate: rateByHour[hour] || null,
      };
    });
  }, [availability, selectedCourt, sessionData]);

  // Check if a starting hour supports the requested duration on the selected court
  const canStartAt = (startHour) => {
    for (let i = 0; i < duration; i++) {
      const found = courtSlots.find((s) => s.hour === startHour + i);
      if (!found || !found.available) return false;
    }
    return true;
  };

  // Compute total price when a slot + duration is selected
  const priceBreakdown = useMemo(() => {
    if (selectedStart === null || !courtSlots.length) return null;
    let total = 0;
    for (let i = 0; i < duration; i++) {
      const s = courtSlots.find((cs) => cs.hour === selectedStart + i);
      if (!s || !s.rate) return null;
      total += s.rate;
    }
    return { total };
  }, [selectedStart, duration, courtSlots]);

  const selectedCourtName = useMemo(() => {
    if (!selectedCourt || !sessionData) return '';
    return sessionData.courts?.find((c) => c.id === selectedCourt)?.name || '';
  }, [selectedCourt, sessionData]);

  const handleSlotClick = (hour) => {
    if (!canStartAt(hour)) return;
    setSelectedStart(hour);
  };

  const handleSubmit = async () => {
    if (!playerName.trim() || !playerPhone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    if (selectedStart === null || !selectedCourt) {
      toast.error('Please pick a time slot');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createCoachingBookingApi(code, {
        player_name: playerName.trim(),
        player_phone: playerPhone.trim(),
        player_email: playerEmail.trim() || undefined,
        court_id: selectedCourt,
        date: selectedDate,
        start_hour: selectedStart,
        duration_hours: duration,
      });
      if (res.data?.id) {
        navigate(`/booking/${res.data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking. The slot may no longer be available.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <Loader2 className="w-8 h-8 animate-spin text-brand-teal" />
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-app-bg">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const promoStart = sessionData?.promo_hours?.start ?? 6;
  const promoEnd = sessionData?.promo_hours?.end ?? 18;
  const coachingRate = sessionData?.coaching_rate ?? 250;

  return (
    <div className="min-h-screen bg-app-bg pb-32" data-testid="coaching-book">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-base sm:text-lg font-semibold text-gray-900 leading-tight">
              Book a Coaching Session
            </h1>
            <p className="text-xs text-gray-500 truncate">
              with Coach {stripCoachPrefix(sessionData?.coach_name)}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4">
        {/* Coaching rate pill */}
        <div className="mt-4" data-testid="rate-pill">
          <div
            className="rounded-2xl px-4 py-3 flex items-center gap-3 border shadow-sm"
            style={{ backgroundColor: '#F0FAF9', borderColor: '#B7E1DE' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: '#D6F0EE' }}
            >
              <Clock className="w-5 h-5" style={{ color: '#0E6F6C' }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#0E6F6C' }}>
                Coaching Court Rate
              </p>
              <p className="text-sm font-semibold text-gray-900">
                ₱{coachingRate}/hr · {formatHourShort(promoStart)}–{formatHourShort(promoEnd)}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Coaching fee arranged separately with your coach.
              </p>
            </div>
          </div>
        </div>

        {/* Date Selector */}
        <div className="py-4" data-testid="date-selector">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Date</p>
          <div className="flex items-center gap-2">
            <div className="flex gap-2 overflow-x-auto timeline-scroll pb-2 flex-1">
              {dateChips.map((d) => {
                const isSelected = d === selectedDate;
                const dayObj = new Date(d + 'T00:00:00');
                return (
                  <button
                    key={d}
                    data-testid={`date-${d}`}
                    onClick={() => setSelectedDate(d)}
                    className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-center transition-all ${
                      isSelected
                        ? 'bg-brand-teal text-white shadow-md'
                        : 'bg-white border border-black/5 text-gray-600 hover:border-brand-teal/30'
                    }`}
                  >
                    <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">
                      {dayObj.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-semibold">{dayObj.getDate()}</div>
                  </button>
                );
              })}
            </div>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="calendar-picker-btn"
                  className="flex-shrink-0 w-12 h-[58px] rounded-xl bg-white border border-black/5 flex items-center justify-center hover:border-brand-teal/30 hover:bg-gray-50 transition-all"
                  title="Pick a specific date"
                >
                  <CalendarDays className="w-5 h-5 text-gray-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : undefined}
                  onSelect={(date) => {
                    if (!date) return;
                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    if (dateStr < todayPHT) return;
                    setSelectedDate(dateStr);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => {
                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    return dateStr < todayPHT;
                  }}
                  fromDate={new Date(todayPHT + 'T00:00:00')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {!dateChips.includes(selectedDate) && (
            <div className="mt-2 px-1">
              <Badge variant="outline" className="bg-brand-teal-tint text-brand-teal border-brand-teal/30">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Badge>
            </div>
          )}
        </div>

        {/* Court Selector */}
        <div className="mb-4" data-testid="court-selector">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Court</p>
          <div className="flex gap-2 overflow-x-auto timeline-scroll">
            {(sessionData?.courts || []).map((court) => {
              const isSelected = court.id === selectedCourt;
              return (
                <button
                  key={court.id}
                  data-testid={`court-${court.id}`}
                  onClick={() => {
                    setSelectedCourt(court.id);
                    setSelectedStart(null);
                  }}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                    isSelected
                      ? 'bg-brand-teal-tint border-2 border-brand-teal text-brand-teal'
                      : 'bg-white border border-black/5 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="text-sm font-medium">{court.name}</span>
                  {isSelected && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Duration Selector */}
        <div className="mb-4" data-testid="duration-selector">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Duration</p>
          <div className="grid grid-cols-3 gap-2">
            {(sessionData?.duration_options || [1, 2, 3]).map((d) => {
              const isSelected = duration === d;
              return (
                <button
                  key={d}
                  data-testid={`duration-${d}h`}
                  onClick={() => {
                    setDuration(d);
                    setSelectedStart(null);
                  }}
                  className={`py-3 rounded-xl text-sm font-semibold transition-all border ${
                    isSelected
                      ? 'bg-brand-teal text-white border-brand-teal shadow-md'
                      : 'bg-white text-gray-600 border-black/5 hover:border-brand-teal/30'
                  }`}
                >
                  {d} {d === 1 ? 'hour' : 'hours'}
                </button>
              );
            })}
          </div>
          {duration > 1 && (
            <p className="text-[11px] text-gray-400 mt-1.5">
              Tip: pick a start time with {duration} consecutive open hours.
            </p>
          )}
        </div>

        {/* Time Slots */}
        <div data-testid="time-slots">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              Choose an Available Court Time
            </p>
            <p className="text-[11px] text-gray-400">{selectedCourtName}</p>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Court availability only. Please coordinate your coaching schedule with Coach {stripCoachPrefix(sessionData?.coach_name)}.
          </p>

          {loadingAvailability ? (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-teal" />
              <p className="text-sm text-gray-500 mt-2">Loading available times…</p>
            </div>
          ) : courtSlots.length === 0 ? (
            <div className="py-8 text-center bg-white rounded-2xl border border-black/5">
              <p className="text-sm text-gray-500">No times available on this court.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {courtSlots.map((slot) => {
                const isSelected =
                  selectedStart !== null &&
                  slot.hour >= selectedStart &&
                  slot.hour < selectedStart + duration;
                const canStart = slot.available && canStartAt(slot.hour);
                const isDisabled = !slot.available;

                let slotClass, labelText;
                if (isSelected) {
                  slotClass = 'bg-brand-teal text-white border-brand-teal shadow-lg scale-[1.03]';
                  labelText = 'Selected';
                } else if (!slot.available) {
                  slotClass = 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed';
                  labelText = 'Unavailable';
                } else if (!canStart) {
                  // Available on its own but not enough consecutive hours to fit duration
                  slotClass =
                    'bg-white border-gray-200 text-gray-300 cursor-not-allowed';
                  labelText = 'Too short';
                } else {
                  slotClass =
                    'bg-white border-status-available/50 text-gray-700 hover:border-brand-teal hover:shadow-sm';
                  labelText = 'Available';
                }

                return (
                  <button
                    key={slot.hour}
                    data-testid={`slot-${slot.hour}`}
                    onClick={() => !isDisabled && handleSlotClick(slot.hour)}
                    disabled={isDisabled || !canStart}
                    className={`h-[72px] rounded-2xl flex flex-col items-center justify-center font-medium transition-all border ${slotClass}`}
                  >
                    <span className="text-sm font-semibold">{formatHour(slot.hour)}</span>
                    <span className="text-[10px] mt-0.5 opacity-70">{labelText}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-status-available" /> Available
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300" /> Unavailable
            </span>
          </div>
        </div>

        {/* Your Information (moved below schedule) */}
        <div className="mt-6 bg-white rounded-2xl border border-black/5 shadow-sm p-4 space-y-3" data-testid="your-info">
          <h2 className="font-semibold text-gray-900 text-sm">Your Information</h2>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" /> Name <span className="text-status-held">*</span>
            </Label>
            <Input
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Enter your name"
              className="rounded-xl mt-1 h-11"
              data-testid="player-name-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Phone Number <span className="text-status-held">*</span>
            </Label>
            <Input
              value={playerPhone}
              onChange={(e) => setPlayerPhone(e.target.value)}
              placeholder="+63 9XX XXX XXXX"
              className="rounded-xl mt-1 h-11"
              data-testid="player-phone-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email{' '}
              <span className="text-gray-400 font-normal">(optional — we&apos;ll send your booking link)</span>
            </Label>
            <Input
              type="email"
              value={playerEmail}
              onChange={(e) => setPlayerEmail(e.target.value)}
              placeholder="your@email.com"
              className="rounded-xl mt-1 h-11"
              data-testid="player-email-input"
            />
          </div>
        </div>

        {/* Persistent Book Now (always visible so users always see the CTA) */}
        <div className="mt-4 space-y-2" data-testid="book-now-block">
          <Button
            data-testid="book-now-btn"
            onClick={handleSubmit}
            disabled={
              submitting ||
              selectedStart === null ||
              !playerName.trim() ||
              !playerPhone.trim()
            }
            className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-2xl py-4 text-base font-semibold shadow-lg active:scale-[0.99] transition-all disabled:opacity-60"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Booking…
              </span>
            ) : selectedStart === null ? (
              'Book Now'
            ) : (
              <>
                Book {formatHourRange(selectedStart, selectedStart + duration)}
                {priceBreakdown ? ` · ₱${priceBreakdown.total}` : ''}
              </>
            )}
          </Button>
          {selectedStart === null && (
            <p className="text-[12px] text-amber-600 text-center">
              Pick a time above to continue.
            </p>
          )}
          {selectedStart !== null && (!playerName.trim() || !playerPhone.trim()) && (
            <p className="text-[12px] text-amber-600 text-center">
              Add your name and phone number above to continue.
            </p>
          )}
        </div>
      </div>

      {/* Sticky bottom summary bar for scroll convenience (only when time is picked) */}
      {selectedStart !== null && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-black/5 p-4 pb-6 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-20"
          data-testid="booking-summary-bar"
        >
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {formatHourRange(selectedStart, selectedStart + duration)}
                {selectedCourtName ? ` · ${selectedCourtName}` : ''}
              </p>
              <p className="text-xs text-gray-500">
                {duration} hour{duration > 1 ? 's' : ''}
                {priceBreakdown ? ` · ₱${priceBreakdown.total}` : ''}
              </p>
            </div>
            <Button
              data-testid="sticky-book-now-btn"
              onClick={handleSubmit}
              disabled={submitting || !playerName.trim() || !playerPhone.trim()}
              className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full px-6 py-3 font-semibold shadow-lg active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                  Booking…
                </>
              ) : (
                <>
                  Book Now
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          {(!playerName.trim() || !playerPhone.trim()) && (
            <p className="max-w-lg mx-auto text-[11px] text-amber-600 mt-2">
              Add your name and phone number above to continue.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
